import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useSites } from '../../../context/SiteContext';
import { useReportVisits } from '../../../hooks/useReportVisits';
import { buildGeo, buildIsp, buildProvinces } from '../../../lib/analytics';
import { formatNumber } from '../../../lib/format';
import EmptyState from '../../../components/dashboard/EmptyState';
import PageHeader from '../../../components/dashboard/PageHeader';
import StatTable from '../../../components/dashboard/StatTable';
import ExportButton from '../../../components/dashboard/ExportButton';

const CN_PROVINCES = [
  '北京', '天津', '上海', '重庆', '河北', '山西', '辽宁', '吉林', '黑龙江',
  '江苏', '浙江', '安徽', '福建', '江西', '山东', '河南', '湖北', '湖南',
  '广东', '海南', '四川', '贵州', '云南', '陕西', '甘肃', '青海', '台湾',
  '内蒙古', '广西', '西藏', '宁夏', '新疆', '香港', '澳门',
];

export default function GeoPage() {
  const { currentSite } = useSites();
  const { ranged, range } = useReportVisits(currentSite);
  const geo = buildGeo(ranged);
  const provinces = buildProvinces(ranged);
  const isps = buildIsp(ranged);
  const geoTotal = geo.reduce((sum, item) => sum + item.value, 0) || 1;
  const max = geo[0]?.value || 1;

  if (!currentSite) {
    return <EmptyState title="先添加一个站点" desc="添加站点后可查看访客来自哪些省市与运营商。" />;
  }

  return (
    <div>
      <PageHeader
        title="地域分布"
        desc="按真实 IP 解析省份、城市和运营商。没有解析到的运营商显示为未知，不会猜测。"
        range={range}
        extra={
          <ExportButton
            filename={`${currentSite.domain}-geo.csv`}
            columns={['类型', '名称', '浏览量']}
            rows={[
              ...provinces.map((p) => ['省份', p.name, formatNumber(p.value)]),
              ...geo.map((g) => ['城市', g.name, formatNumber(g.value)]),
              ...isps.map((i) => ['运营商', i.name, formatNumber(i.value)]),
            ]}
          />
        }
      />

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-900">省份访问量</h2>
          <div className="mt-4 h-80">
            {provinces.length === 0 ? (
              <p className="text-sm text-foreground-500">暂无地域数据</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={provinces.slice(0, 10)} layout="vertical" margin={{ left: 16 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={56} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#2F9D6A" radius={[0, 6, 6, 0]} name="访问量" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">网络服务商</h2>
          </div>
          <StatTable
            columns={['运营商', '浏览量']}
            rows={isps.map((d) => [d.name, formatNumber(d.value)])}
          />
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="text-sm font-semibold text-foreground-900">省份热力</h2>
        <p className="mt-1 text-xs text-foreground-500">未接入商业 IP 库前，按已识别省份着色。</p>
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-9 gap-2">
          {CN_PROVINCES.map((name) => {
            const value = provinces.find((p) => p.name === name)?.value || 0;
            const heat = max ? value / (provinces[0]?.value || max) : 0;
            return (
              <div
                key={name}
                className="rounded-md px-2 py-2 text-center text-[11px] font-medium"
                style={{
                  background: value ? `rgba(226, 90, 60, ${0.18 + heat * 0.72})` : '#f4efe8',
                  color: heat > 0.55 ? '#fff' : '#4a3f35',
                }}
                title={`${name} ${value}`}
              >
                <div>{name}</div>
                <div className="mt-0.5 opacity-80">{value || '—'}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="px-5 py-3 border-b border-background-200">
          <h2 className="text-sm font-semibold text-foreground-900">城市排行</h2>
        </div>
        <StatTable
          columns={['城市', '浏览量', '占比']}
          rows={geo.map((g) => [
            g.name,
            formatNumber(g.value),
            `${Math.round((g.value / geoTotal) * 100)}%`,
          ])}
          empty="暂无城市数据"
        />
      </div>
    </div>
  );
}
